import { authService } from '@/api/services';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useLanguage } from '@/i18n/LanguageContext';
import { cn } from '@/lib/utils';
import {
  CreditCard,
  FileBox,
  LayoutDashboard,
  LineChart,
  Menu,
  Receipt,
  Target,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UserProfile from './UserProfile';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  active: boolean;
  onClick?: () => void;
}

const NavItem = ({ icon, label, href, active, onClick }: NavItemProps) => {
  return (
    <Link to={href} onClick={onClick}>
      <Button
        variant="ghost"
        className={cn(
          'mb-1 w-full justify-start gap-2',
          active && 'bg-secondary text-primary font-medium'
        )}
      >
        {icon}
        {label}
      </Button>
    </Link>
  );
};

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { t } = useLanguage();
  const isAuthenticated = authService.isAuthenticated();

  const navItems = [
    { icon: <LayoutDashboard size={20} />, label: t('nav.dashboard'), href: '/' },
    { icon: <CreditCard size={20} />, label: t('nav.accounts'), href: '/accounts' },
    { icon: <Target size={20} />, label: t('nav.goals'), href: '/goals' },
    { icon: <Receipt size={20} />, label: t('nav.transactions'), href: '/transactions' },
    { icon: <FileBox size={20} />, label: t('nav.vault'), href: '/vault' },
    { icon: <LineChart size={20} />, label: t('nav.market'), href: '/market' },
  ];

  const handleLogout = () => {
    authService.removeToken();
    navigate('/login');
    setSheetOpen(false);
  };

  const handleNavigation = () => {
    setSheetOpen(false);
  };

  // Helper function to check if a path is active
  const isPathActive = (path: string) => {
    // For the home route, only match exactly
    if (path === '/') {
      return location.pathname === path;
    }
    // For other routes, match if the path starts with the route path
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="flex h-screen flex-col">
      {/* Mobile Header */}
      <header className="flex h-14 items-center justify-between border-b px-4 md:hidden">
        <div className="text-primary text-lg font-bold">{t('app.name')}</div>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu size={24} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0">
            <div className="flex h-full flex-col">
              <div className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <div className="text-primary text-lg font-bold">{t('app.name')}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6">
                <nav className="space-y-1">
                  {navItems.map((item) => (
                    <NavItem
                      key={item.href}
                      icon={item.icon}
                      label={item.label}
                      href={item.href}
                      active={isPathActive(item.href)}
                      onClick={handleNavigation}
                    />
                  ))}
                </nav>
                {isAuthenticated && <UserProfile isMobile onNavigation={handleNavigation} />}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden w-64 border-r md:flex md:flex-col">
          <div className="p-6">
            <div className="text-primary mb-8 text-xl font-bold">{t('app.name')}</div>
          </div>
          <div className="flex-1 overflow-y-auto px-6">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavItem
                  key={item.href}
                  icon={item.icon}
                  label={item.label}
                  href={item.href}
                  active={isPathActive(item.href)}
                />
              ))}
            </nav>
            {isAuthenticated && <UserProfile />}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
};

export default MainLayout;
